// src/utils/customerValidation.js
// Customer form validation — mirrors the backend Joi rules in offlineSales/validations/customer.validation.js

export const digitsOf = (value) => String(value || '').replace(/\D/g, '');
// '+94' alone (the phone input's default prefix) counts as no phone
export const isBarePhone = (value) => digitsOf(value).replace(/^94/, '').length < 4;
export const phoneKey = (value) => digitsOf(value).slice(-9);
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const CUS_CODE_RE = /^[A-Za-z0-9-]+$/;
export const todayStr = () => new Date().toISOString().split('T')[0];

// Sri Lankan numbers need exactly 9 digits after +94; other countries (+code) 8–15 digits; blank/'+94' = no phone
export const isValidPhone = (value) => {
  if (isBarePhone(value)) return true;
  const compact = String(value).replace(/[\s-]/g, '');
  const digits = digitsOf(value);
  if (compact.startsWith('+') && !compact.startsWith('+94')) return digits.length >= 8 && digits.length <= 15;
  return digits.replace(/^(0094|94|0)/, '').length === 9;
};

/**
 * @returns {object} { field: message } for every failing field (empty object when valid)
 */
export const validateCustomerForm = ({ form, openingBalance, obLocked }, t) => {
  const errors = {};
  const code = form.cusCode.trim();
  if (code && (!CUS_CODE_RE.test(code) || code.length > 20)) errors.cusCode = t('offlineSales.cusCodeInvalid');
  if (!form.name.trim()) errors.name = t('offlineSales.nameRequired');
  if (!isValidPhone(form.phone)) errors.phone = t('offlineSales.invalidPhone');
  if (!isValidPhone(form.secondaryPhone)) errors.secondaryPhone = t('offlineSales.invalidPhone');
  else if (!isBarePhone(form.phone) && !isBarePhone(form.secondaryPhone) && phoneKey(form.phone) === phoneKey(form.secondaryPhone)) {
    errors.secondaryPhone = t('offlineSales.secondarySameAsPrimary');
  }
  if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) errors.email = t('offlineSales.invalidEmail');
  if (form.creditLimit !== '' && !(parseFloat(form.creditLimit) >= 0)) errors.creditLimit = t('offlineSales.mustBeNonNegative');
  if (form.creditTermDays !== '' && !(Number.isInteger(Number(form.creditTermDays)) && Number(form.creditTermDays) >= 0)) {
    errors.creditTermDays = t('offlineSales.mustBeWholeNumber');
  }
  if (!obLocked) {
    // Amount and date are a pair — either one makes the other required
    const hasAmount = String(openingBalance.amount ?? '').trim() !== '';
    const hasDate = !!openingBalance.asOfDate;
    if (hasAmount && !(parseFloat(openingBalance.amount) >= 0)) errors.obAmount = t('offlineSales.mustBeNonNegative');
    if (hasDate && !hasAmount) errors.obAmount = t('offlineSales.obAmountRequired');
    if (hasAmount && !hasDate) errors.obDate = t('offlineSales.obDateRequired');
    if (hasDate && openingBalance.asOfDate > todayStr()) errors.obDate = t('offlineSales.obDateFuture');
  }
  return errors;
};

// Backend `errors[].field` (Joi path) → customer form field
export const BACKEND_FIELD_MAP = {
  'openingBalance.asOfDate': 'obDate',
  'openingBalance.amount': 'obAmount',
  openingBalance: 'obDate',
};
